import { useLocalStorage } from '@uidotdev/usehooks';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';

import CreateNewMetadataDialog from '@/shared/components/dialogs/CreateNewMetadataDialog';
import WaveUIProgressDialog from '@/shared/components/dialogs/WaveUIProgressDialog';
import MetadataFormAccordion from '@/shared/components/forms/MetadataField/components/MetadataFormAccordion';
import WaveUINotificationBar from '@/shared/components/utilities/WaveUINotificationBar';
import WaveUIPageLoader from '@/shared/components/utilities/WaveUIPageLoader';
import WaveUIAccordion from '@/shared/components/widgets/WaveUIAccordion';
import useModalDialog from '@/shared/hooks/useModalDialog';
import useNotificationBar from '@/shared/hooks/useNotificationBar';
import useProgressDialog from '@/shared/hooks/useProgressDialog';
import { AtomModel_VIEW } from '@/shared/types/atom.type';
import DialogName from '@/shared/types/dialogName.enum';

import ShippingProvidersSelect from '../components/FulfilmentProvidersSelect/FulfilmentProvidersSelect';
import useFulfilmentProvidersSelect from '../components/FulfilmentProvidersSelect/hooks/useFulfilmentProvidersSelect';
import UpdateInventoryItemsDialog from '../components/UpdateInventoryItemsDialog';
import useCreateMetadata from '../hooks/useCreateMetadata';
import useFetchInventoryItem from '../hooks/useFetchInventoryItem';
import useFetchInventoryMetadata from '../hooks/useFetchInventoryMetadata';
import useMetadataOperations from '../hooks/useMetadataOperations';
import InventoryItemsTable_v2 from './components/InventoryItemsTable_v2/InventoryItemsTable_v2';
import RemoveInventoryItemsDialog from './components/RemoveInventoryItemsDialog';
import RemoveInventoryItemsWidget from './components/RemoveInventoryItemsWidget';
import Title from './components/Title';
import useInventoryItemsOperations from './hooks/useInventoryItemsOperations';
import useUpdateInventory from './hooks/useUpdateInventory';

const InventoryEdit: FC = () => {
  const { t } = useTranslation(['inventory']);
  const id = useParams().id || '';

  const { dialog, onUpdateDialogState } = useModalDialog<unknown, DialogName>();
  const { progressDialog, onUpdateProgressDialog } = useProgressDialog();
  const { notificationBar, onUpdateNotificationBar } = useNotificationBar();

  const { refetchMetadata, onCreateNewMetadata, setRefetchMetadata } =
    useCreateMetadata({
      onUpdateDialogState,
      onUpdateProgressDialog,
      onUpdateNotificationBar,
    });

  const { metadata } = useFetchInventoryMetadata({
    refetchMetadata,
    setRefetchMetadata,
  });

  const { inventory: initialInventoryData } = useFetchInventoryItem({
    inventoryId: id,
  });

  const [refreshTableIndex, setRefreshTableIndex] = useState(0);
  const [inventoryItemsTotal, setInventoryItemsTotal] = useState(0);

  const {
    isFormValid,
    inventoryDataToSave,
    // collectInventoryDetails,
    // setCollectInventoryDetails,
    // getInventoryDetails,
    // getInventoryItemsDetails,
  } = useMetadataOperations();

  const { onUpdateInventoryItems, onRemoveInventoryItems } =
    useInventoryItemsOperations({
      inventoryId: id,
      setRefetchMetadata,
      onUpdateDialogState,
      onUpdateProgressDialog,
      onUpdateNotificationBar,
      setRefreshTableIndex,
    });

  const { onInventoryUpdate, onAddItemsToInventory } = useUpdateInventory({
    inventoryId: id,
    isFormValid,
    inventoryDataToSave,
    onUpdateProgressDialog,
    onUpdateNotificationBar,
    setRefreshTableIndex,
  });

  const [stashedMetadata, setStashedMetadata] = useLocalStorage<Record<
    string,
    unknown
  > | null>('stashedInventoryMetadata-edit', null);

  const fulfilmentProvidersSelectState = useFulfilmentProvidersSelect();

  const [inventory, setInventory] = useState<{
    required: Record<string, unknown>;
    optional: Record<string, unknown>;
  }>({
    required: {},
    optional: {},
  });

  const [inventoryDetails, setInventoryDetails] = useState<{
    required: Record<string, unknown>;
    optional: Record<string, unknown>;
    initialData?: Record<string, unknown>;
    qty: string;
  }>({
    required: {},
    optional: {},
    qty: '0',
    initialData: {
      is_fulfilable: true,
    },
  });

  return (
    <div>
      <Title
        id={id}
        loading={progressDialog?.open}
        handleMetadataCreate={() => {
          onUpdateDialogState(DialogName.CreateMetadata, {
            open: true,
            errors: [],
          });
        }}
        handleSaveChanges={() => {
          void onInventoryUpdate({
            ...inventory.required,
            ...inventory.optional,
          });

          if (parseInt(inventoryDetails.qty) > 0) {
            void onAddItemsToInventory({
              qty: inventoryDetails.qty,
              ...inventoryDetails.initialData,
              ...inventoryDetails.required,
              ...inventoryDetails.optional,
            });
          }
        }}
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          alignItems: 'flex-start',
          gap: 2,
        }}
      >
        {(!metadata || !inventory) && <WaveUIPageLoader />}

        {metadata && initialInventoryData && (
          <>
            <MetadataFormAccordion
              accordionProps={{ title: 'Inventory' }}
              metadata={metadata}
              levelConstraint='INVENTORY'
              fields={inventory}
              initialData={initialInventoryData}
              getValues={(values) => {
                setInventory((prev) => ({
                  ...prev,
                  required: { ...prev.required, ...values.required },
                  optional: { ...prev.optional, ...values.optional },
                }));
              }}
            />

            <WaveUIAccordion
              title={t('inventory:inventoryItemsDetails')}
              defaultExpanded
            >
              <TextField
                placeholder='Set items quantity ...'
                required
                label='Inventory quantity'
                variant='outlined'
                value={inventoryDetails.qty}
                inputProps={{ type: 'number' }}
                onChange={(e) =>
                  setInventoryDetails((prev) => ({
                    ...prev,
                    qty: e.target.value,
                  }))
                }
              />
              <ShippingProvidersSelect
                fulfilmentProvidersSelectState={fulfilmentProvidersSelectState}
              />

              <MetadataFormAccordion
                accordionProps={{
                  title: null,
                  elevation: 0,
                  summaryProps: { hidden: true },
                  sx: { mx: -2 },
                }}
                metadata={metadata}
                initialData={inventoryDetails.initialData}
                levelConstraint='ATOM'
                fields={inventoryDetails}
                getValues={(values) => {
                  setInventoryDetails((prev) => ({
                    ...prev,
                    required: { ...prev.required, ...values.required },
                    optional: { ...prev.optional, ...values.optional },
                  }));
                }}
              />
            </WaveUIAccordion>

            <RemoveInventoryItemsWidget
              inventoryItemsTotal={inventoryItemsTotal}
              onRemoveInventoryItems={(numberOfItemsToRemove) => {
                onUpdateDialogState(
                  DialogName.REMOVE_INVENTORY_ITEMS_CONFIRMATION,
                  {
                    open: true,
                    data: numberOfItemsToRemove,
                  },
                );
              }}
            />

            <Box sx={{ gridColumn: '1/-1' }}>
              <InventoryItemsTable_v2
                refreshTableIndex={refreshTableIndex}
                inventoryId={id}
                stashedMetadata={stashedMetadata}
                metadataDefinitions={metadata.metadata_definitions}
                setInventoryItemsTotal={setInventoryItemsTotal}
                onUpdateSelectedItems={(selectedItems) =>
                  onUpdateDialogState(DialogName.UpdateInventoryItems, {
                    open: true,
                    data: selectedItems,
                  })
                }
              />
            </Box>
          </>
        )}
      </Box>

      {dialog?.createMetadata?.open && (
        <CreateNewMetadataDialog
          metadata={metadata}
          module='inventory'
          errors={dialog.createMetadata.errors}
          onClose={() =>
            onUpdateDialogState(DialogName.CreateMetadata, {
              open: false,
              loading: false,
            })
          }
          proceed={(formToSave) => {
            void onCreateNewMetadata(formToSave);
          }}
        />
      )}

      {dialog?.updateInventoryItems?.open && metadata && (
        <UpdateInventoryItemsDialog
          metadata={metadata}
          stashedMetadata={stashedMetadata}
          errors={dialog?.updateInventoryItems?.errors}
          onClose={() => {
            onUpdateDialogState(DialogName.UpdateInventoryItems, {
              open: false,
              errors: [],
            });
          }}
          onStash={setStashedMetadata}
          proceed={(metadataDetails) => {
            const { data } = dialog?.updateInventoryItems as {
              data: AtomModel_VIEW[];
            };
            void onUpdateInventoryItems(
              stashedMetadata || metadataDetails,
              data,
            );
            setStashedMetadata(null);
          }}
        />
      )}

      {dialog?.removeInventoryItemsConfirmation?.open && (
        <RemoveInventoryItemsDialog
          numberOfItemsToRemove={
            dialog?.removeInventoryItemsConfirmation?.data as number
          }
          onClose={() => {
            onUpdateDialogState(
              DialogName.REMOVE_INVENTORY_ITEMS_CONFIRMATION,
              {
                open: false,
                errors: [],
              },
            );
          }}
          proceed={() => {
            const { data } = dialog?.removeInventoryItemsConfirmation as {
              data: number;
            };
            void onRemoveInventoryItems(data);
          }}
        />
      )}

      <WaveUINotificationBar
        message={notificationBar.message}
        severity={notificationBar.severity}
        open={notificationBar.open}
        close={() => onUpdateNotificationBar({ open: false })}
      />

      <WaveUIProgressDialog
        title={progressDialog?.title}
        progress={progressDialog?.progress || 0}
        open={progressDialog?.open || false}
      />
    </div>
  );
};

export default InventoryEdit;
